import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InputArea from './InputArea';
import { dateFormat, fullDateFormat, timeFormat } from 'Util/DateUtils';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import { getFileName, getFileNameArrayWithExtension } from 'Util/DataUtils';
import { parseText } from 'Routes/field/detail/CommentSection/Utils';
import nayaLogo from 'Assets/img/naya_logo_32@2x.png';
import icDownloadBasic from 'Assets/img/ic_download_20_basic@2x.png';
import icDownloadStrong from 'Assets/img/ic_download_20_strong@2x.png';
import icClose from 'Assets/img/ic_close_20_basic@2x.png';
import icLoop from 'Assets/img/ic_loop_20_basic@2x.png';
import IntlMessages from 'Util/IntlMessages';
import FileSizeArea from './FileSizeArea';
import { throttle } from 'lodash';
import ImageDialog from './ImageDialog';
import { useFileUpload } from '../hooks/useFileUpload';
import { getReadableFileSize } from 'Util/FileUtils';
import { getExtensionImages2 } from 'Util/Extension2';
import Notice from './Notice';
import Faq from './Faq';
import BasicButton from 'Components/Button/BasicButton';
import { useIntl } from 'react-intl';

const CustomerSupportChatContent = ({ comments, getList, isLoading, refreshInputComment, faq, postFaq, isOpenFaq, setIsOpenFaq }) => {
  const { locale } = useSelector(state => state.settings.locale);
  const scrollRef = useRef(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [nowImages, setNowImages] = useState([]);
  const [nowIndex, setNowIndex] = useState(-1);
  const [nowDate, setNowDate] = useState(null);
  const [nowIsMine, setNowIsMine] = useState(false);
  const { formatMessage } = useIntl();
  const pageDown = useCallback(() => {
    const ref = scrollRef.current;
    ref.scrollTop = 0;
  }, []);
  const { loadingItems, startUpload, cancelUpload } = useFileUpload(pageDown);

  const closedFaq = useMemo(() => {
    try {
      const value = localStorage.getItem('closedFaq');
      return value ? JSON.parse(value) : false;
    } catch (e) {
      return false;
    }
  }, []);

  useEffect(() => {
    if (closedFaq) {
      setIsOpenFaq(false);
    }
  }, [closedFaq, setIsOpenFaq]);

  const handleScroll = useCallback(
    throttle(() => {
      const ref = scrollRef.current;
      if (Math.abs(ref.scrollTop) > ref.scrollHeight - ref.clientHeight - 100) {
        if (!isLoading) {
          getList();
        }
      }
    }, 300),
    [isLoading, getList]
  );

  useEffect(() => {
    const ref = scrollRef.current;
    ref?.addEventListener('scroll', handleScroll);
    return () => {
      ref?.removeEventListener('scroll', handleScroll);
    };
  }, [isLoading, handleScroll]);

  const renderTextWithLinks = text => {
    if (!text) return '';

    const LINK_STYLE = {
      color: '#0567f9',
      textDecoration: 'underline',
    };

    const createLink = (url, index) => {
      const href = url.startsWith('http') ? url : `http://${url.startsWith('www.') ? url : url}`;

      return (
        <a key={index} href={href} target='_blank' rel='noopener noreferrer' style={LINK_STYLE}>
          {url}
        </a>
      );
    };

    return parseText(text, createLink);
  };

  const getImageWidth = (length, i) => {
    if (length === 1) return '100%'; // 1개
    if (length % 3 === 0) return '33.3%'; // 3의 배수
    if ((length - 4) % 3 === 0) return i < 4 ? '50%' : '33.3%'; // 4 + 3의 배수
    return i < 2 ? '50%' : '33.3%'; // 2 + 3의 배수
  };

  const getImageBorder = (length, i) => {
    let border = {};
    if (length % 3 === 0) {
      if (i % 3 !== 0) {
        border.borderLeft = '1px solid #DBDBDB';
      }
      if (i > 2) {
        border.borderTop = '1px solid #DBDBDB';
      }
      return border;
    }
    if ((length - 4) % 3 === 0) {
      if (i === 1) {
        border.borderLeft = '1px solid #DBDBDB';
      }
      if (i !== 0 && i !== 2 && i % 3 !== 1) {
        border.borderLeft = '1px solid #DBDBDB';
      }
      if (i > 1) {
        border.borderTop = '1px solid #DBDBDB';
      }
      return border;
    }
    if (i !== 0 && i % 3 !== 2) {
      border.borderLeft = '1px solid #DBDBDB';
    }
    if (i > 1) {
      border.borderTop = '1px solid #DBDBDB';
    }
    return border;
  };

  const renderAutoReply = autoReply => {
    const { answer, buttonText, image, linkType } = autoReply;
    return (
      <>
        {image && <img className='customer-support-chat-item-content-auto-reply-image' src={image} alt='auto reply image' />}
        {answer && <div className='customer-support-chat-item-content-auto-reply-answer'>{answer}</div>}
        {buttonText &&
          linkType &&
          (linkType === 'BUDGET' || linkType === 'NEXT_AUTO_REPLY' || linkType === 'HIMPEL_EVENT' ? (
            <BasicButton
              type='subtle'
              textMessage={buttonText}
              style={{ width: '100%' }}
              onClick={() => {
                if (linkType === 'BUDGET') {
                  window.open('/admin/order', '_blank');
                } else if (linkType === 'NEXT_AUTO_REPLY') {
                  if (autoReply.targetId) {
                    postFaq(autoReply.targetId, () => {
                      pageDown();
                      refreshInputComment();
                    });
                  }
                } else if (linkType === 'HIMPEL_EVENT') {
                  window.open(formatMessage({ id: 'order.main.body.promotion.link' }), '_blank');
                }
              }}
            />
          ) : (
            <div className='customer-support-chat-item-content-auto-reply-button'>
              <IntlMessages id='customer.support.chat.auto.reply.button.text' />
            </div>
          ))}
      </>
    );
  };

  const renderContent = ({ text, images, attachments, isMine, createdAt, autoReply }) => {
    const backgroundColor = isMine ? 'var(--Color-Blue-10, #CCE6FF)' : 'var(--Surface-Default-1-Rest, #FFF)';
    const color = 'var(--Text-Basic, #414141)';
    const fileBorder = isMine ? null : 'var(--Border-Default-1-Rest, #e4e4e4)';
    const icDownload = isMine ? icDownloadStrong : icDownloadBasic;

    if (autoReply) {
      return (
        <div className='customer-support-chat-item-content-auto-reply' style={{ backgroundColor, color }}>
          {renderAutoReply(autoReply)}
        </div>
      );
    }
    if (attachments && attachments.length > 0) {
      return (
        <div style={{ maxWidth: '290px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {attachments.map((x, i) => (
            <div
              key={`attachments_${i}`}
              style={{
                backgroundColor,
                color,
                ...(fileBorder && { border: fileBorder }),
              }}
              className='customer-support-chat-item-content-attachment'
            >
              <div className='customer-support-chat-item-content-attachment-image-wrapper'>
                <img src={getExtensionImages2(getFileNameArrayWithExtension(x)[1])} alt='file image' style={{ width: '20px', height: '20px' }} />
              </div>
              <div className='customer-support-chat-item-content-attachment-text'>
                <div className='customer-support-chat-item-content-attachment-text-name' style={{ color }}>
                  {getFileName(x)}
                </div>
                <FileSizeArea file={x} />
              </div>
              <div className='customer-support-chat-item-content-attachment-button-wrapper cursor-pointer no-drag' onClick={() => saveAs(x, getFileName(x))}>
                <img src={icDownload} alt='download' style={{ width: '20px', height: '20px' }} />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (images && images.length === 1) {
      return (
        <div className='customer-support-chat-item-content-image-wrapper'>
          <div className='customer-support-chat-item-content-image-wrapper-list'>
            <div
              style={{ ...getImageBorder(images.length, 0) }}
              onClick={() => {
                setOpenImageModal(true);
                setNowImages(images);
                setNowIndex(0);
                setNowDate(createdAt);
                setNowIsMine(isMine);
              }}
            >
              <img src={images[0]} alt='image' style={{ objectFit: 'contain', minWidth: '64px', minHeight: '64px', maxWidth: '282px', maxHeight: '282px' }} />
            </div>
          </div>
        </div>
      );
    }
    if (images && images.length > 0) {
      let length = images.length;
      return (
        <div className='customer-support-chat-item-content-image-wrapper'>
          <div className='customer-support-chat-item-content-image-wrapper-list'>
            {images.map((x, i) => {
              return (
                <div
                  key={`images_${i}`}
                  style={{
                    width: getImageWidth(length, i),
                    aspectRatio: '1 / 1',
                    ...getImageBorder(length, i),
                  }}
                  onClick={() => {
                    setOpenImageModal(true);
                    setNowImages(images);
                    setNowIndex(i);
                    setNowDate(createdAt);
                    setNowIsMine(isMine);
                  }}
                >
                  <img
                    alt=''
                    src={x}
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return (
      <div className='customer-support-chat-item-content-text' style={{ backgroundColor, color }}>
        {renderTextWithLinks(text || '')}
      </div>
    );
  };

  const renderLoadingItem = item => {
    const backgroundColor = 'var(--Color-Blue-10, #CCE6FF)';
    const color = 'var(--Text-Basic, #414141)';

    return (
      <div key={item.id} className='customer-support-chat-item-content-mine'>
        <div style={{ maxWidth: '290px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <div
            style={{
              backgroundColor,
              color,
            }}
            className='customer-support-chat-item-content-attachment'
          >
            <div className='customer-support-chat-item-content-attachment-image-wrapper'>
              <img src={icLoop} alt='loop' style={{ width: '20px', height: '20px' }} />
            </div>
            <div className='customer-support-chat-item-content-attachment-text'>
              <div className='customer-support-chat-item-content-attachment-text-name' style={{ color }}>
                {item.filename}
              </div>
              <div className='customer-support-chat-item-content-attachment-text-size' style={{ color }}>
                {`${getReadableFileSize(item.loaded)} / ${getReadableFileSize(item.total)}`}
              </div>
            </div>
            <div className='customer-support-chat-item-content-attachment-button-wrapper cursor-pointer no-drag' onClick={() => cancelUpload(item.id)}>
              <img src={icClose} alt='close' style={{ width: '20px', height: '20px' }} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMineComment = comment => {
    const { text, images, attachments, createdAt } = comment;
    return (
      <div className='customer-support-chat-item-content-mine'>
        <div className='customer-support-chat-item-content-mine-time'>{timeFormat(createdAt, locale)}</div>
        {renderContent({ text, images, attachments, isMine: true, createdAt })}
      </div>
    );
  };

  const renderOtherComment = comment => {
    const { text, images, attachments, createdAt, autoReply } = comment;
    return (
      <div className='customer-support-chat-item-content-other'>
        <img src={nayaLogo} alt='naya logo' className='customer-support-chat-item-content-other-logo' />
        <div className='customer-support-chat-item-content-other-content-wrapper'>
          <div className='customer-support-chat-item-content-other-content'>
            <div className='customer-support-chat-item-content-other-content-title'>
              <IntlMessages id='customer.support.chat.default.message' />
            </div>
            {renderContent({
              text,
              images,
              attachments,
              isMine: false,
              createdAt,
              autoReply,
            })}
          </div>
          <div className='customer-support-chat-item-content-other-content-time'>{timeFormat(createdAt, locale)}</div>
        </div>
      </div>
    );
  };

  const renderComment = (comment, index) => {
    const { _id, domain, createdAt } = comment;
    const isMine = domain !== 'ADMIN';
    const nextDate = dateFormat(comments?.[index + 1]?.createdAt, locale);
    const isSameDate = nextDate === dateFormat(createdAt, locale);
    return (
      <Fragment key={_id}>
        {isMine ? renderMineComment(comment) : renderOtherComment(comment)}
        {!isSameDate && (
          <div className='customer-support-chat-item-date'>
            <div className='customer-support-chat-item-date-line' />
            {fullDateFormat(createdAt, locale)}
            <div className='customer-support-chat-item-date-line' />
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <div className='customer-support-chat-page-content'>
      <div className='customer-support-chat-notice-wrapper'>
        <Notice />
      </div>
      <div className='customer-support-chat-list' ref={scrollRef}>
        {loadingItems.map(renderLoadingItem)}
        {comments.map(renderComment)}
      </div>
      {isOpenFaq && (
        <Faq
          faq={faq}
          postFaq={postFaq}
          setIsOpenFaq={setIsOpenFaq}
          refresh={() => {
            pageDown();
            refreshInputComment();
          }}
        />
      )}
      <InputArea
        refresh={() => {
          pageDown();
          refreshInputComment();
        }}
        startUpload={startUpload}
      />
      <ImageDialog
        modal={openImageModal}
        onClose={() => {
          setOpenImageModal(false);
          setNowImages([]);
          setNowIndex(-1);
          setNowDate(null);
          setNowIsMine(false);
        }}
        images={nowImages}
        initIndex={nowIndex}
        date={nowDate}
        isMine={nowIsMine}
      />
    </div>
  );
};

export default CustomerSupportChatContent;
